import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
 ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwElementsBaseUrlsModel } from 'projects/gtw-elements/src/app/shared/_models/gtw-elements-base-urls.model';
import { ExtEntityMaintenanceService } from './ext-entity-maintenance.service';
import {
  Country,
  EINData,
  EntityType,
  ExtEntitySaveRequest,
  ExtEntitySaveResponse,
  FilterParams,
  HomeOfficeEntity,
  State,
  ValidationResult,
} from './models/ext-entity.interface';
import { GtwSnackbarService } from 'projects/gtw-elements/src/app/shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'app-ext-entity-maintenance',
    templateUrl: './ext-entity-maintenance.component.html',
    styleUrls: ['./ext-entity-maintenance.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExtEntityMaintenanceComponent implements OnInit, OnDestroy {
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };
  header_data: any = {};
  filterParams!: FilterParams;
  rowData: any = {};
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  extEntityForm!: UntypedFormGroup;
  entityTypeList: EntityType[] = [];
  countryList: Country[] = [];
  stateList: State[] = [];
  holeList: HomeOfficeEntity[] = [];
  einList: EINData[] = [];
  minimized = false;
  isNew = true;
  isSaveClicked = false;
  title = '';
  selectedHomeOffice = '';
  isCompany = true;
  isDisregarded = false;
  isUSCountry = false;
  isRetirePlan = false;

  private isInitialized = false;
  private originalRowData: any = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private gtwSnackbarService: GtwSnackbarService,
    private extEntityService: ExtEntityMaintenanceService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.originalRowData = this.rowData;
    this.setupFormSubscriptions();
    this.initializeService();
    this.loadInitialData();
    this.gtwWorkspace.setTabActive.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
      this.rowData = this.originalRowData;
      this.initializeService();
    });
    this.gtwGlobal.globalParamsChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
      this.rowData = this.originalRowData;
      this.initializeService();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header?.globalParams || {};
    this.USER_SETTINGS = header?.userSettingsData || {};
    this.SERVER_CONFIG = header?.serverConfigData || {};
    this.GENERAL_CONFIG = header?.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen) as FilterParams;

    const bridgedRowData = (activeScreen as any)?.data?.rowData;
    this.rowData = (bridgedRowData && typeof bridgedRowData === 'object' && Object.keys(bridgedRowData).length > 0)
      ? bridgedRowData
      : {};
  }

  private initializeForm(): void {
    this.extEntityForm = this.fb.group({
      COMP_PER: ['C', Validators.required],
      ENTITY_PERSON_NAME: [''],
      PERSON_NAME: [''],
      SSN_EIN: [''],
      SSN: [''],
      DISREGARDED: ['N'],
      LE_TYPE: ['', Validators.required],
      RETIRE_PLAN_KEY: ['0'],
      TYPE_OF_ENTITY_KEY: ['C'],
      COUNTRY_NAME: [''],
      REG_ADDRESS_LINE1: [''],
      REG_ADDRESS_LINE2: [''],
      REG_ADDRESS_CITY: [''],
      REG_ADDRESS_STATE: [''],
      STATE_NAME: [''],
      REG_ADDRESS_ZIP: [''],
      HO_EIN: [''],
      HO_LEID: [''],
      HO_LE_NAME: [''],
      ho_le: [''],
      // Example date field for future use
      // EFFECTIVE_DATE: ['', Validators.required],
    });

    // Initialize component properties based on form values
    this.isCompany = this.extEntityForm.get('COMP_PER')?.value === 'C';
    this.isDisregarded = this.extEntityForm.get('DISREGARDED')?.value === 'Y';
    this.isRetirePlan = this.extEntityForm.get('RETIRE_PLAN_KEY')?.value === '1';
  }

  private initializeService(): void {
    if (this.baseURLs && this.globalService && this.filterParams) {
      this.extEntityService.initialize(
        this.baseURLs,
        this.globalService,
        this.filterParams
      );
    }
  }

  private setupFormSubscriptions(): void {
    this.extEntityForm
      .get('COMP_PER')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isCompany = value === 'C';
        this.updateConditionalValidators();
      });

    this.extEntityForm
      .get('DISREGARDED')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isDisregarded = value === 'Y';
        this.resetHomeOffice();
        this.updateConditionalValidators();
      });

    this.extEntityForm
      .get('COUNTRY_NAME')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isUSCountry = value === 'US - United States of America';
        this.updateConditionalValidators();
      });

    this.extEntityForm
      .get('RETIRE_PLAN_KEY')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isRetirePlan = value === '1';
      });
  }

  private loadInitialData(): void {
    if (!this.filterParams || !this.globalService) {
      return;
    }

    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    const paramData = {
      taxYear: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.globalService.jcd_key,
      sso_id: ssoId,
      jcd_ta_key: this.globalService.jcd_ta_key,
      screen_key: this.globalService.screen_key
    };

    forkJoin({
      entityTypes: this.extEntityService.loadAllEntityTypes(paramData),
      countries: this.extEntityService.loadCountryList(paramData),
      states: this.extEntityService.loadStateList(paramData),
      homeOffices: this.extEntityService.loadHomeOfficeList(paramData),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.entityTypeList = this.formatEntityTypeData(results.entityTypes || []);
          this.countryList = this.formatCountryData(results.countries || []);
          this.stateList = this.formatStateData(results.states || []);
          this.holeList = results.homeOffices || [];

          this.setInitialFormValues();
          this.isInitialized = true;
        },
        error: (error) => {
          console.error('Error loading dropdown data:', error);
          this.entityTypeList = [];
          this.countryList = [];
          this.stateList = [];
          this.holeList = [];

          this.setInitialFormValues();
          this.isInitialized = true;
        }
      });
  }

  private setInitialFormValues(): void {
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      this.isNew = true;
      this.title = 'Add External Entity';
      this.extEntityForm.patchValue({
        COMP_PER: 'C',
      });
    } else {
      this.isNew = false;
      this.title = `Modify External Entity: ${this.rowData.LEID}`;

      let leTypeCode = this.rowData.LE_TYPE;
      if (leTypeCode && leTypeCode.includes(' ')) {
        leTypeCode = leTypeCode.substring(0, leTypeCode.indexOf(' '));
      }

      // Match country value to the exact format used by <option [value]>
      let countryValue = this.rowData.COUNTRY_NAME || '';
      if (countryValue && !countryValue.includes(' - ')) {
        const match = this.countryList.find(c => c.CODE_SHORT_DESC === countryValue);
        if (match) {
          countryValue = `${match.CODE_SHORT_DESC} - ${match.CODE_DESCRIPTION}`;
        }
      }

      // Match state value to the exact format used by <option [value]>
      let stateValue = this.rowData.STATE_NAME || '';
      if (stateValue && !stateValue.includes(' - ')) {
        const match = this.stateList.find(s => s.CODE === stateValue);
        if (match) {
          stateValue = `${match.CODE} - ${match.NAME}`;
        }
      }

      // Match home office to the HO_CODE in the dropdown
      let hoLeValue = '';
      if (this.rowData.HO_LEID && this.rowData.LEID !== this.rowData.HO_LEID) {
        const match = this.holeList.find(h =>
          String(h.HO_CODE ?? '').startsWith(this.rowData.HO_LEID)
        );
        if (match) {
          hoLeValue = String(match.HO_CODE);
        }
      }

      this.extEntityForm.patchValue({
        COMP_PER: this.rowData.COMP_PER,
        ENTITY_PERSON_NAME:
          this.rowData.COMP_PER === 'C'
            ? this.rowData.ENTITY_PERSON_NAME
            : null,
        PERSON_NAME:
          this.rowData.COMP_PER === 'P'
            ? this.rowData.ENTITY_PERSON_NAME
            : null,
        SSN_EIN: this.rowData.COMP_PER === 'C' ? this.rowData.SSN_EIN : null,
        SSN: this.rowData.COMP_PER === 'P' ? this.rowData.SSN_EIN : null,
        DISREGARDED: this.rowData.LEID !== this.rowData.HO_LEID ? 'Y' : 'N',
        LE_TYPE: leTypeCode,
        RETIRE_PLAN_KEY: this.rowData.RETIRE_PLAN_KEY || '0',
        TYPE_OF_ENTITY_KEY: this.rowData.TYPE_OF_ENTITY_KEY || 'C',
        COUNTRY_NAME: countryValue,
        REG_ADDRESS_LINE1: this.rowData.REG_ADDRESS_LINE1,
        REG_ADDRESS_LINE2: this.rowData.REG_ADDRESS_LINE2,
        REG_ADDRESS_CITY: this.rowData.REG_ADDRESS_CITY,
        REG_ADDRESS_STATE: this.rowData.REG_ADDRESS_STATE,
        STATE_NAME: stateValue,
        REG_ADDRESS_ZIP: this.rowData.REG_ADDRESS_ZIP,
        HO_EIN: this.rowData.HO_EIN,
        HO_LEID: this.rowData.HO_LEID,
        HO_LE_NAME: this.rowData.HO_LE_NAME,
        ho_le: hoLeValue,
      }, { emitEvent: false });

      if (this.rowData.HO_LEID) {
        this.selectedHomeOffice = `${this.rowData.HO_LEID}/${this.rowData.HO_CDR_NO}/${this.rowData.HO_REPORTING_PERIOD} - ${this.rowData.HO_LE_NAME}`;
      }
    }

    this.updateComponentProperties();
    this.updateConditionalValidators();
  }

  private updateComponentProperties(): void {
    this.isCompany = this.extEntityForm.get('COMP_PER')?.value === 'C';
    this.isDisregarded = this.extEntityForm.get('DISREGARDED')?.value === 'Y';
    this.isRetirePlan = this.extEntityForm.get('RETIRE_PLAN_KEY')?.value === '1';
    this.isUSCountry = this.extEntityForm.get('COUNTRY_NAME')?.value === 'US - United States of America';
  }

  private updateConditionalValidators(): void {
    const compPer = this.extEntityForm.get('COMP_PER')?.value;
    const countryName = this.extEntityForm.get('COUNTRY_NAME')?.value;
    const disregarded = this.extEntityForm.get('DISREGARDED')?.value;

    this.extEntityForm.get('ENTITY_PERSON_NAME')?.clearValidators();
    this.extEntityForm.get('PERSON_NAME')?.clearValidators();
    this.extEntityForm.get('SSN_EIN')?.clearValidators();
    this.extEntityForm.get('SSN')?.clearValidators();
    this.extEntityForm.get('ho_le')?.clearValidators();
    this.extEntityForm.get('REG_ADDRESS_LINE1')?.clearValidators();
    this.extEntityForm.get('REG_ADDRESS_CITY')?.clearValidators();
    this.extEntityForm.get('STATE_NAME')?.clearValidators();
    this.extEntityForm.get('REG_ADDRESS_ZIP')?.clearValidators();

    if (compPer === 'C') {
      this.extEntityForm
        .get('ENTITY_PERSON_NAME')
        ?.setValidators([Validators.required]);

      if (disregarded === 'Y') {
        this.extEntityForm.get('ho_le')?.setValidators([Validators.required]);
      }
    } else if (compPer === 'P') {
      this.extEntityForm
        .get('PERSON_NAME')
        ?.setValidators([Validators.required]);
    }

    if (countryName === 'US - United States of America') {
      if (compPer === 'C') {
        this.extEntityForm
          .get('SSN_EIN')
          ?.setValidators([Validators.required, Validators.minLength(9)]);
      } else if (compPer === 'P') {
        this.extEntityForm
          .get('SSN')
          ?.setValidators([Validators.required, Validators.minLength(9)]);
      }

      this.extEntityForm
        .get('REG_ADDRESS_LINE1')
        ?.setValidators([Validators.required]);
      this.extEntityForm
        .get('REG_ADDRESS_CITY')
        ?.setValidators([Validators.required]);
      this.extEntityForm
        .get('STATE_NAME')
        ?.setValidators([Validators.required]);
      this.extEntityForm
        .get('REG_ADDRESS_ZIP')
        ?.setValidators([Validators.required]);
    } else if (countryName) {
      this.extEntityForm
        .get('REG_ADDRESS_LINE1')
        ?.setValidators([Validators.required]);
      this.extEntityForm
        .get('REG_ADDRESS_CITY')
        ?.setValidators([Validators.required]);
    }

    // Update validity without emitting events to prevent infinite recursion
    Object.keys(this.extEntityForm.controls).forEach((key) => {
      this.extEntityForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  onHomeOfficeChange(event: EventEmitter<Object> | any): void {
    const hoLe = event.target.value;
    if (hoLe && this.filterParams && this.globalService) {
      const le = hoLe.substring(0, 15);
      const taxYear = this.filterParams.tax_year;
      const scenario = this.filterParams.scenario;
      const jcdKey = this.globalService.jcd_key;

      this.extEntityService
        .setHomeOfficeEIN(taxYear, scenario, jcdKey, le)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: EINData[]) => {
          this.einList = data;
          if (data.length > 0) {
            this.extEntityForm.patchValue({
              HO_EIN: data[0].HO_EIN,
            });
          }
        });
    }
  }

  onRetirePlanChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    this.extEntityForm.get('RETIRE_PLAN_KEY')?.setValue(isChecked ? '1' : '0');
    this.isRetirePlan = isChecked;
    this.resetHomeOffice();
    this.updateConditionalValidators();
  }
  
  onDisregardedChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    this.extEntityForm.get('DISREGARDED')?.setValue(isChecked ? 'Y' : 'N');
    this.isDisregarded = isChecked;
    this.resetHomeOffice();
    this.updateConditionalValidators();
  }

  private resetHomeOffice(): void {
    if (this.isNew && !this.isDisregarded) {
      this.extEntityForm.patchValue({
        HO_LE_NAME: '',
        ho_le: '',
        HO_EIN: '',
      });
    }
  }

  save(): void {
    if (this.isSaveClicked) {
      return;
    }

    this.isSaveClicked = true;

    const formValue = this.extEntityForm.value;
    const ssnEin = formValue.SSN_EIN || formValue.SSN;

    if (
      this.isNew &&
      this.isNumber(ssnEin) &&
      this.filterParams &&
      this.globalService
    ) {
      const taxYear = this.filterParams.tax_year;
      const scenario = this.filterParams.scenario;
      const jcdKey = this.globalService.jcd_key;

      this.extEntityService
        .checkSSNEINExists(taxYear, scenario, jcdKey, ssnEin)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: string | any[]) => {
          const exists = data.length > 0 && data[0].SSN_EIN;

          if (exists) {
            if (
              confirm(
                'The EIN/SSN already exists. Do you want to continue? Please confirm.'
              )
            ) {
              this.processSave();
            } else {
              this.isSaveClicked = false;
            }
          } else {
            this.processSave();
          }
        });
    } else {
      this.processSave();
    }
  }

  private processSave(): void {
    const validationResult = this.validateFormData();

    if (!validationResult.isValid) {
      this.gtwSnackbarService.openSnackBar(
        validationResult.errorMessage || 'Validation failed',
        'gtw-snackbar--red'
      );
      this.isSaveClicked = false;
      return;
    }

    const formValue = this.extEntityForm.value;
    const extSetupSaveDtls = this.buildSaveRequest(formValue);
    const settings = this.buildSettingsObject();

    this.extEntityService
      .saveExternalEntitySetup(settings, this.globalService, this.filterParams, extSetupSaveDtls)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          if (result.errorMessage && result.errorMessage !== 'null') {
            this.isSaveClicked = false;
            this.gtwSnackbarService.openSnackBar(
              result.errorMessage,
              'gtw-snackbar--red'
            );
          } else {
            this.gtwSnackbarService.openSnackBar(
              'External Entity saved successfully',
              'gtw-snackbar--green'
            );
            setTimeout(() => {
              this.cancel();
              setTimeout(() => {
                const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
                if (activeScreen?.screen_key) {
                  this.gtwWorkspace.refreshGridData.next(activeScreen);
                }
              }, 500);
            }, 3000);
          }
        },
        (error: any) => {
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar(
            'Error occurred during external entity save. Please try again.',
            'gtw-snackbar--red'
          );
        }
      );
  }

  private validateFormData(): ValidationResult {
    const formValue = this.extEntityForm.value;

    if (formValue.COMP_PER === 'C' && !formValue.ENTITY_PERSON_NAME) {
      return { isValid: false, errorMessage: 'Entity Name is mandatory' };
    }

    if (
      formValue.COMP_PER === 'C' &&
      formValue.DISREGARDED === 'Y' &&
      !formValue.ho_le
    ) {
      return {
        isValid: false,
        errorMessage: 'Must select a Home Office for a disregarded Entity',
      };
    }

    if (formValue.COMP_PER === 'P' && !formValue.PERSON_NAME) {
      return { isValid: false, errorMessage: 'Person Name is mandatory' };
    }

    if (!formValue.LE_TYPE) {
      return { isValid: false, errorMessage: 'Entity Type is mandatory' };
    }

    if (formValue.COUNTRY_NAME === 'US - United States of America') {
      if (formValue.COMP_PER === 'C' && !formValue.SSN_EIN) {
        return {
          isValid: false,
          errorMessage: 'EIN is mandatory, if the country is US',
        };
      }

      if (formValue.COMP_PER === 'P' && !formValue.SSN) {
        return {
          isValid: false,
          errorMessage: 'SSN is mandatory, if the country is US',
        };
      }

      if (!formValue.REG_ADDRESS_LINE1) {
        return { isValid: false, errorMessage: 'Address Line1 is mandatory' };
      }

      if (!formValue.REG_ADDRESS_CITY) {
        return { isValid: false, errorMessage: 'City is mandatory' };
      }

      if (!formValue.STATE_NAME) {
        return { isValid: false, errorMessage: 'State is mandatory' };
      }

      if (!formValue.REG_ADDRESS_ZIP) {
        return {
          isValid: false,
          errorMessage: 'Postal / Zip Code is mandatory',
        };
      }
    } else if (formValue.COUNTRY_NAME) {
      if (!formValue.REG_ADDRESS_LINE1) {
        return { isValid: false, errorMessage: 'Address Line1 is mandatory' };
      }

      if (!formValue.REG_ADDRESS_CITY) {
        return { isValid: false, errorMessage: 'City is mandatory' };
      }
    }

    if (formValue.SSN_EIN && formValue.SSN_EIN.toString().length < 9) {
      return { isValid: false, errorMessage: 'EIN must be 9 characters.' };
    }

    if (formValue.SSN && formValue.SSN.toString().length < 9) {
      return { isValid: false, errorMessage: 'SSN must be 9 characters.' };
    }

    return { isValid: true };
  }

  private buildSaveRequest(formValue: any): ExtEntitySaveRequest {
    const extSetupSaveDtls: ExtEntitySaveRequest = {
      isNew: this.isNew ? 'Y' : 'N',
      LE_KEY: this.isNew ? '' : this.rowData?.object_id || '',
      COMBINATION_KEY: this.isNew
        ? ''
        : (this.rowData as any)?.COMBINATION_KEY || '',
      LEID: this.isNew ? '' : this.rowData?.LEID || '',
      HO_LEID: this.extractHomeOfficeId(formValue.ho_le),
      COMP_PER: formValue.COMP_PER,
      entity_person_name: formValue.ENTITY_PERSON_NAME || formValue.PERSON_NAME,
      SSN_EIN: formValue.SSN_EIN || formValue.SSN,
      DISREGARDED: formValue.DISREGARDED,
      LE_TYPE: this.extractEntityTypeCode(formValue.LE_TYPE),
      RETIRE_PLAN: formValue.RETIRE_PLAN_KEY,
      TYPE_OF_ENTITY: formValue.TYPE_OF_ENTITY_KEY,
      REG_ADDRESS_COUNTRY: this.extractCountryCode(formValue.COUNTRY_NAME),
      REG_ADDRESS_LINE1: formValue.REG_ADDRESS_LINE1,
      REG_ADDRESS_LINE2: formValue.REG_ADDRESS_LINE2 || '',
      REG_ADDRESS_CITY: formValue.REG_ADDRESS_CITY,
      REG_ADDRESS_STATE: this.extractStateCode(formValue),
      REG_ADDRESS_ZIP: formValue.REG_ADDRESS_ZIP || '',
    };

    return extSetupSaveDtls;
  }

  private buildSettingsObject(): any {
    return {
      'tax_year': this.filterParams.tax_year,
      'scenario': this.filterParams.scenario,
      'jcd_key': this.globalService.jcd_key,
      'sso_id': this.USER_SETTINGS.user.sso_id,
    };
  }

  private extractHomeOfficeId(hoLe: string): string {
    if (!hoLe) return '';
    const firstSlashIndex = hoLe.indexOf('/');
    return firstSlashIndex > 0 ? hoLe.substring(0, firstSlashIndex) : hoLe;
  }

  private extractEntityTypeCode(leType: string): string {
    if (!leType) return '';
    const spaceIndex = leType.indexOf(' ');
    return spaceIndex > 0 ? leType.substring(0, spaceIndex) : leType;
  }

  private extractCountryCode(countryName: string): string {
    if (!countryName) return '';
    const spaceIndex = countryName.indexOf(' ');
    return spaceIndex > 0 ? countryName.substring(0, spaceIndex) : countryName;
  }

  private extractStateCode(formValue: any): string {
    const countryCode = this.extractCountryCode(formValue.COUNTRY_NAME);

    if (countryCode === 'US') {
      const stateName = formValue.STATE_NAME;
      if (stateName) {
        const spaceIndex = stateName.indexOf(' ');
        return spaceIndex > 0 ? stateName.substring(0, spaceIndex) : stateName;
      }
    } else {
      return formValue.REG_ADDRESS_STATE || '';
    }

    return '';
  }

  reset(): void {
    this.extEntityForm.reset({
      COMP_PER: 'C',
      DISREGARDED: 'N',
      RETIRE_PLAN_KEY: '0',
      TYPE_OF_ENTITY_KEY: 'C',
    });
    this.isSaveClicked = false;
  }

  private isNumber(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }

  /**
   * Helper method to ensure HomeOfficeEntity data is properly formatted
   */
  private formatHomeOfficeData(data: any[]): HomeOfficeEntity[] {
    return data.map(item => ({
      HO_CODE: item.HO_CODE,
      HO_DESC: item.HO_DESC || ''
    }));
  }

  /**
   * Helper method to ensure EntityType data is properly formatted
   */
  private formatEntityTypeData(data: any[]): EntityType[] {
    if (!this.isValidDataStructure(data, ['LE_TYPE_CODE', 'LE_TYPE'])) {
      return [];
    }
    
    return data.map(item => ({
      LE_TYPE_CODE: item.LE_TYPE_CODE || '',
      LE_TYPE: item.LE_TYPE || ''
    }));
  }

  /**
   * Helper method to ensure Country data is properly formatted
   */
  private formatCountryData(data: any[]): Country[] {
    if (!this.isValidDataStructure(data, ['CODE_SHORT_DESC', 'CODE_DESCRIPTION'])) {
      return [];
    }
    
    return data.map(item => ({
      CODE_SHORT_DESC: item.CODE_SHORT_DESC || '',
      CODE_DESCRIPTION: item.CODE_DESCRIPTION || ''
    }));
  }

  /**
   * Helper method to ensure State data is properly formatted
   */
  private formatStateData(data: any[]): State[] {
    if (!this.isValidDataStructure(data, ['CODE', 'NAME'])) {
      return [];
    }
    
    return data.map(item => ({
      CODE: item.CODE || '',
      NAME: item.NAME || ''
    }));
  }

  /**
   * Helper method to validate data structure
   */
  private isValidDataStructure(data: any[], requiredFields: string[]): boolean {
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
    
    const firstItem = data[0];
    return requiredFields.every(field => firstItem.hasOwnProperty(field));
  }

  getFormControl(controlName: string): AbstractControl | null {
    return this.extEntityForm.get(controlName);
  }

  hasError(controlName: string, errorType: string): boolean {
    const control = this.getFormControl(controlName);
    return !!(
      control &&
      control.hasError(errorType) &&
      (control.dirty || control.touched)
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
